import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  CheckCircle,
  FileText,
  ShieldCheck,
  UploadSimple,
  Users,
  Waveform,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import PublicContactDialog from "@/components/PublicContactDialog";
import BrandLockup from "@/components/BrandLockup";

const frictionPoints = [
  {
    number: "01",
    title: "Details fade",
    copy: "The nuance that made the conversation useful is the first thing memory drops.",
  },
  {
    number: "02",
    title: "Context fragments",
    copy: "One call lives in a transcript, another in the CRM, and the real story stays in your head.",
  },
  {
    number: "03",
    title: "Commitments slip",
    copy: "Promises, blockers, and decision dynamics disappear between one follow-up and the next.",
  },
  {
    number: "04",
    title: "Admin takes over",
    copy: "The call ends, but the documentation work has only just started.",
  },
];

const pillars = [
  {
    icon: Users,
    label: "Relationship awareness",
    title: "Never lose the customer story.",
    copy: "Every conversation becomes part of one evolving relationship, so you always know what changed, what remains unresolved, and where things truly stand.",
    outcome: "Complete continuity, before every next call.",
  },
  {
    icon: Brain,
    label: "Next-move intelligence",
    title: "Know what should happen next.",
    copy: "Callsect turns buying signals, objections, risks, commitments, and decision dynamics into a clear move grounded in what was actually said.",
    outcome: "Less guesswork. Better timing. Stronger follow-through.",
  },
  {
    icon: FileText,
    label: "Effortless execution",
    title: "Leave the documentation to Callsect.",
    copy: "CRM-ready notes are created in your own voice, so insight becomes usable without reconstructing the conversation by hand.",
    outcome: "Move forward while the context is still fresh.",
  },
];

const steps = [
  {
    number: "01",
    title: "Add the conversation",
    copy: "Upload the call and connect it to the right customer relationship.",
  },
  {
    number: "02",
    title: "Recover the full context",
    copy: "Callsect identifies what changed, links it to previous conversations, and surfaces the signals that matter.",
  },
  {
    number: "03",
    title: "Move forward with clarity",
    copy: "Use the recommended next move, copy the CRM-ready documentation, and enter the next conversation fully prepared.",
  },
];

const timelineEvents = [
  {
    date: "18 Jul",
    title: "Renewal decision call",
    copy: "Interest strengthened. Finance approval is now the final blocker.",
    tag: "Latest",
  },
  {
    date: "08 Jul",
    title: "Commercial follow-up",
    copy: "The customer requested a revised timeline and named the budget owner.",
    tag: "Commitment",
  },
  {
    date: "24 Jun",
    title: "Initial evaluation",
    copy: "Clear product interest, but no decision process or success criteria yet.",
    tag: "Starting point",
  },
];

export default function LandingPage() {
  const { user, loading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestBeta = searchParams.get("request") === "beta";
  const requestedEmail = (searchParams.get("email") || "").trim().toLowerCase();
  const [contactDialog, setContactDialog] = useState(requestBeta ? "beta" : null);

  useEffect(() => {
    if (requestBeta) setContactDialog("beta");
  }, [requestBeta]);

  const closeContactDialog = () => {
    setContactDialog(null);
    if (!requestBeta && !requestedEmail) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("request");
    nextParams.delete("email");
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[#050507] text-white">
      <header className="relative z-30 border-b border-white/[0.06] bg-[#050507]/80 px-5 py-4 backdrop-blur-xl sm:px-8">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <Link to="/" className="brand-lockup-link" aria-label="Callsect home">
            <BrandLockup className="brand-lockup--landing" variant="dark" />
          </Link>

          <div className="flex items-center gap-3">
            <span className="hidden rounded-full border border-indigo-400/20 bg-indigo-400/[0.07] px-3 py-1 text-xs font-medium text-indigo-200 sm:inline-flex">
              Private beta
            </span>
            {!loading && user ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.06] px-4 py-2 text-sm font-medium transition hover:border-white/20 hover:bg-white/10"
              >
                Open dashboard
                <ArrowRight size={15} />
              </Link>
            ) : (
              <Link
                to="/login"
                className="rounded-lg px-3 py-2 text-sm text-slate-300 transition hover:bg-white/5 hover:text-white sm:px-4"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>

      <main>
        <section className="relative px-5 pb-24 pt-20 sm:px-8 sm:pt-28 lg:pb-32">
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute left-[-10%] top-[-12rem] h-[34rem] w-[34rem] rounded-full bg-indigo-700/20 blur-[130px]" />
            <div className="absolute right-[-8%] top-[4rem] h-[28rem] w-[28rem] rounded-full bg-cyan-700/10 blur-[130px]" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#050507]" />
          </div>

          <div className="relative mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-[1.04fr_0.96fr]">
            <div>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.035] px-3 py-1.5 text-xs uppercase tracking-[0.2em] text-slate-300">
                <Waveform size={14} className="text-indigo-400" />
                Relationship intelligence for customer-facing teams
              </div>

              <h1 className="max-w-3xl text-5xl font-semibold leading-[0.98] tracking-[-0.055em] sm:text-6xl lg:text-7xl">
                <span className="block">Know the relationship.</span>
                <span className="block bg-gradient-to-r from-indigo-300 via-violet-300 to-cyan-300 bg-clip-text text-transparent">
                  Know the next move.
                </span>
                <span className="block">Write nothing.</span>
              </h1>

              <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-400">
                Callsect turns every customer conversation into connected relationship
                context, a clear next move, and CRM-ready documentation in your own voice.
              </p>

              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setContactDialog("beta")}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-indigo-400/40 bg-indigo-600 px-5 py-3.5 font-medium shadow-[0_0_40px_rgba(79,70,229,0.22)] transition hover:bg-indigo-500"
                >
                  Request beta access
                  <ArrowRight size={17} weight="bold" />
                </button>

                <Link
                  to={user ? "/dashboard" : "/login"}
                  className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] px-5 py-3.5 font-medium text-slate-200 transition hover:border-white/20 hover:bg-white/[0.08]"
                >
                  {user ? "Open dashboard" : "I already have access"}
                </Link>
              </div>

              <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-500">
                <TrustPoint>Invite-only beta</TrustPoint>
                <TrustPoint>Multilingual by design</TrustPoint>
                <TrustPoint>No raw audio retained</TrustPoint>
              </div>
            </div>

            <RelationshipSnapshot />
          </div>
        </section>

        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-24 sm:px-8">
          <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[0.82fr_1.18fr] lg:items-start">
            <div className="lg:sticky lg:top-28">
              <Eyebrow>What gets lost after the call</Eyebrow>
              <h2 className="mt-4 max-w-xl text-4xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Every conversation moves the relationship.
                <span className="mt-2 block text-slate-500">
                  Most of that progress disappears.
                </span>
              </h2>
              <p className="mt-6 max-w-lg text-lg leading-8 text-slate-400">
                The call may be over, but the relationship is still moving. Without a
                connected system, the context that should guide the next step gets buried
                across memory, transcripts, notes, and CRM fields.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {frictionPoints.map((point) => (
                <article
                  key={point.number}
                  className="rounded-2xl border border-white/[0.08] bg-[#09090d] p-6"
                >
                  <div className="font-mono text-xs text-indigo-400">{point.number}</div>
                  <h3 className="mt-6 text-xl font-semibold">{point.title}</h3>
                  <p className="mt-3 leading-7 text-slate-400">{point.copy}</p>
                </article>
              ))}

              <div className="sm:col-span-2 rounded-2xl border border-indigo-400/20 bg-indigo-500/[0.07] p-6 sm:p-8">
                <div className="text-sm font-semibold text-indigo-200">
                  Callsect keeps the relationship alive after the call ends.
                </div>
                <p className="mt-3 max-w-3xl leading-7 text-slate-300">
                  Not as another isolated summary, but as usable continuity for the next
                  decision, follow-up, and conversation.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-5 py-24 sm:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <Eyebrow>The Callsect difference</Eyebrow>
              <h2 className="mt-4 text-4xl font-semibold tracking-[-0.04em] sm:text-5xl">
                One conversation.
                <span className="block bg-gradient-to-r from-indigo-300 via-violet-300 to-cyan-300 bg-clip-text text-transparent">
                  The full relationship.
                </span>
              </h2>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-400">
                Callsect connects understanding, decision, and execution in one continuous
                workflow.
              </p>
            </div>

            <div className="mt-12 grid gap-5 lg:grid-cols-3">
              {pillars.map(({ icon: Icon, label, title, copy, outcome }) => (
                <article
                  key={label}
                  className="group flex min-h-full flex-col rounded-2xl border border-white/[0.08] bg-[#0a0a0e] p-7 transition hover:border-indigo-400/25 hover:bg-[#0c0c12]"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-indigo-400/20 bg-indigo-500/10 text-indigo-300">
                    <Icon size={22} />
                  </div>

                  <div className="mt-8 text-xs font-semibold uppercase tracking-[0.22em] text-indigo-300">
                    {label}
                  </div>
                  <h3 className="mt-3 text-2xl font-semibold tracking-tight">{title}</h3>
                  <p className="mt-4 leading-7 text-slate-400">{copy}</p>

                  <div className="mt-8 border-t border-white/[0.07] pt-5 text-sm leading-6 text-slate-300 lg:mt-auto">
                    {outcome}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-24 sm:px-8">
          <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[0.78fr_1.22fr] lg:items-center">
            <div>
              <Eyebrow>From one call to everything you need</Eyebrow>
              <h2 className="mt-4 text-4xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Understand what happened.
                <span className="block text-slate-500">Then make it useful.</span>
              </h2>
              <p className="mt-6 max-w-xl text-lg leading-8 text-slate-400">
                Dissection, coaching, CRM documentation, and relationship context are not
                separate outputs. They are different parts of the same next decision.
              </p>

              <div className="mt-8 space-y-4">
                <ProofPoint>See what changed and why it matters.</ProofPoint>
                <ProofPoint>Turn the evidence into a practical next move.</ProofPoint>
                <ProofPoint>Carry the context forward without rewriting it.</ProofPoint>
              </div>
            </div>

            <ProductPreview />
          </div>
        </section>

        <section className="px-5 py-24 sm:px-8">
          <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-[0.82fr_1.18fr] lg:items-center">
            <div>
              <Eyebrow>Relationship timeline</Eyebrow>
              <h2 className="mt-4 text-4xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Every conversation becomes part of the story.
              </h2>
              <p className="mt-6 max-w-xl text-lg leading-8 text-slate-400">
                Callsect does not treat calls as isolated events. It connects them so the
                next conversation starts with the history, not a blank page.
              </p>

              <div className="mt-8 grid gap-3 text-sm text-slate-300 sm:grid-cols-2">
                <ProofPoint>How objections evolve</ProofPoint>
                <ProofPoint>What was promised</ProofPoint>
                <ProofPoint>Who is involved</ProofPoint>
                <ProofPoint>How buying intent changes</ProofPoint>
              </div>
            </div>

            <TimelinePreview />
          </div>
        </section>

        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-24 sm:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="grid gap-12 lg:grid-cols-[0.75fr_1.25fr]">
              <div>
                <Eyebrow>How it works</Eyebrow>
                <h2 className="mt-4 text-4xl font-semibold tracking-[-0.04em] sm:text-5xl">
                  Upload.
                  <br />
                  Understand.
                  <br />
                  Move forward.
                </h2>
                <p className="mt-5 max-w-lg leading-7 text-slate-400">
                  No disconnected tools. No generic summary to reorganise. No manual
                  reconstruction before the next call.
                </p>
              </div>

              <div className="divide-y divide-white/[0.07] rounded-2xl border border-white/[0.08] bg-[#09090d] px-6 sm:px-8">
                {steps.map((step) => (
                  <div
                    key={step.number}
                    className="grid gap-3 py-7 sm:grid-cols-[4rem_1fr]"
                  >
                    <div className="font-mono text-sm text-indigo-400">{step.number}</div>
                    <div>
                      <h3 className="text-xl font-semibold">{step.title}</h3>
                      <p className="mt-2 leading-7 text-slate-400">{step.copy}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-5 py-24 sm:px-8">
          <div className="mx-auto grid max-w-7xl gap-8 rounded-3xl border border-white/[0.08] bg-gradient-to-br from-indigo-950/40 via-[#0b0b10] to-cyan-950/20 p-8 sm:p-12 lg:grid-cols-[0.78fr_1.22fr] lg:items-center">
            <div>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-emerald-400/20 bg-emerald-400/10 text-emerald-300">
                <ShieldCheck size={23} />
              </div>
              <Eyebrow className="mt-7">Private by design</Eyebrow>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
                Your conversations stay yours.
              </h2>
            </div>

            <div>
              <p className="max-w-3xl text-lg leading-8 text-slate-300">
                Audio is processed temporarily and removed after analysis. You stay in
                control of your Callsect data, with export and deletion available whenever
                you need them.
              </p>

              <div className="mt-7 grid gap-3 text-sm sm:grid-cols-3">
                <TrustCard>Temporary audio processing</TrustCard>
                <TrustCard>Export and deletion controls</TrustCard>
                <TrustCard>Approved-account access</TrustCard>
              </div>

              <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <Link className="text-indigo-300 hover:text-indigo-200" to="/privacy">
                  Privacy Notice
                </Link>
                <Link
                  className="text-indigo-300 hover:text-indigo-200"
                  to="/data-handling"
                >
                  Data handling
                </Link>
                <Link className="text-indigo-300 hover:text-indigo-200" to="/terms">
                  Private Beta Terms
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="px-5 pb-24 sm:px-8">
          <div className="relative mx-auto overflow-hidden rounded-3xl border border-indigo-400/20 bg-[#09090d] px-8 py-14 text-center sm:px-12 sm:py-20">
            <div className="pointer-events-none absolute left-1/2 top-[-14rem] h-[30rem] w-[44rem] -translate-x-1/2 rounded-full bg-indigo-700/20 blur-[120px]" />
            <div className="relative mx-auto max-w-4xl">
              <Eyebrow>Your next conversation</Eyebrow>
              <h2 className="mt-5 text-4xl font-semibold tracking-[-0.045em] sm:text-5xl lg:text-6xl">
                It should not start from memory.
                <span className="mt-2 block bg-gradient-to-r from-indigo-300 via-violet-300 to-cyan-300 bg-clip-text text-transparent">
                  It should start from certainty.
                </span>
              </h2>
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-slate-400">
                Bring the full relationship, the right next move, and ready-to-use
                documentation into every customer conversation.
              </p>

              <button
                type="button"
                onClick={() => setContactDialog("beta")}
                className="mt-9 inline-flex items-center justify-center gap-2 rounded-xl border border-indigo-400/40 bg-indigo-600 px-6 py-3.5 font-medium shadow-[0_0_40px_rgba(79,70,229,0.22)] transition hover:bg-indigo-500"
              >
                Request beta access
                <ArrowRight size={17} weight="bold" />
              </button>

              <div className="mt-4 text-sm text-slate-500">Invite-only private beta</div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/[0.06] px-5 py-10 sm:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <BrandLockup className="brand-lockup--footer" variant="dark" />
            <span>© 2026 Callsect</span>
          </div>

          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link className="hover:text-white" to="/privacy">
              Privacy
            </Link>
            <Link className="hover:text-white" to="/terms">
              Beta Terms
            </Link>
            <Link className="hover:text-white" to="/data-handling">
              Data handling
            </Link>
            <button
              type="button"
              className="hover:text-white"
              onClick={() => setContactDialog("support")}
            >
              Support
            </button>
          </div>
        </div>
      </footer>

      <PublicContactDialog
        kind={contactDialog || "beta"}
        open={Boolean(contactDialog)}
        initialEmail={contactDialog === "beta" ? requestedEmail : ""}
        onClose={closeContactDialog}
      />
    </div>
  );
}

function TrustPoint({ children }) {
  return (
    <span className="inline-flex items-center gap-2">
      <CheckCircle size={16} weight="fill" className="text-emerald-400" />
      {children}
    </span>
  );
}

function ProofPoint({ children }) {
  return (
    <div className="flex items-start gap-3">
      <CheckCircle
        size={18}
        weight="fill"
        className="mt-0.5 shrink-0 text-emerald-400"
      />
      <span className="leading-6 text-slate-300">{children}</span>
    </div>
  );
}

function TrustCard({ children }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-black/10 px-4 py-3 text-slate-300">
      <div className="flex items-start gap-2">
        <CheckCircle
          size={16}
          weight="fill"
          className="mt-0.5 shrink-0 text-emerald-400"
        />
        <span>{children}</span>
      </div>
    </div>
  );
}

function Eyebrow({ children, className = "" }) {
  return (
    <div
      className={`text-xs font-semibold uppercase tracking-[0.28em] text-indigo-400 ${className}`}
    >
      {children}
    </div>
  );
}

function RelationshipSnapshot() {
  return (
    <div className="relative mx-auto w-full max-w-xl">
      <div className="absolute -inset-5 rounded-[2rem] bg-gradient-to-br from-indigo-500/20 to-cyan-500/5 blur-2xl" />

      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#09090d] shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/[0.07] px-5 py-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
              Relationship snapshot
            </div>
            <div className="mt-1 font-semibold">Acme Industrial · Renewal</div>
          </div>

          <div className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
            Updated
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 border-b border-white/[0.07] p-4">
          {[
            ["Buying intent", "78"],
            ["Relationship health", "84"],
            ["Next-step clarity", "91"],
          ].map(([label, value]) => (
            <div
              key={label}
              className="rounded-xl border border-white/[0.06] bg-white/[0.025] p-3"
            >
              <div className="text-[9px] uppercase tracking-[0.15em] text-slate-500">
                {label}
              </div>
              <div className="mt-2 text-2xl font-medium">{value}</div>
            </div>
          ))}
        </div>

        <div className="grid gap-3 p-4 sm:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.025] p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-indigo-300">
              What changed
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              Interest strengthened since the last call. Finance approval is now the
              final blocker.
            </p>
          </div>

          <div className="rounded-xl border border-indigo-400/15 bg-indigo-500/[0.06] p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-indigo-300">
              Next move
            </div>
            <h3 className="mt-3 font-semibold">Secure the decision call</h3>
            <p className="mt-2 text-xs leading-5 text-slate-400">
              Send the revised timeline today and involve finance while momentum is high.
            </p>
          </div>
        </div>
      </div>

      <div className="absolute -bottom-7 -left-3 hidden items-center gap-3 rounded-xl border border-white/10 bg-[#0c0c11]/95 px-4 py-3 shadow-xl backdrop-blur sm:flex">
        <UploadSimple size={20} className="text-cyan-300" />
        <div>
          <div className="text-xs text-slate-500">CRM note</div>
          <div className="text-sm font-medium">Ready in your voice</div>
        </div>
        <CheckCircle
          size={18}
          weight="fill"
          className="ml-3 text-emerald-400"
        />
      </div>
    </div>
  );
}

function ProductPreview() {
  return (
    <div className="relative">
      <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-indigo-500/15 to-cyan-500/5 blur-2xl" />

      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#09090d] shadow-2xl">
        <div className="flex flex-col gap-4 border-b border-white/[0.07] px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
              Callsect output
            </div>
            <div className="mt-1 font-semibold">Renewal decision call</div>
          </div>

          <div className="flex flex-wrap gap-2 text-[10px] font-semibold uppercase tracking-wider">
            <span className="rounded-full border border-indigo-400/20 bg-indigo-400/[0.08] px-2.5 py-1 text-indigo-200">
              Dissection
            </span>
            <span className="rounded-full border border-violet-400/20 bg-violet-400/[0.08] px-2.5 py-1 text-violet-200">
              Coaching
            </span>
            <span className="rounded-full border border-cyan-400/20 bg-cyan-400/[0.08] px-2.5 py-1 text-cyan-200">
              CRM ready
            </span>
          </div>
        </div>

        <div className="grid gap-3 p-4 md:grid-cols-3">
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.025] p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-indigo-300">
              What mattered
            </div>
            <h3 className="mt-3 font-semibold">The blocker changed</h3>
            <p className="mt-2 text-xs leading-5 text-slate-400">
              Value is no longer in question. The decision now depends on finance timing.
            </p>
          </div>

          <div className="rounded-xl border border-indigo-400/15 bg-indigo-500/[0.06] p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-indigo-300">
              Best next move
            </div>
            <h3 className="mt-3 font-semibold">Create a decision path</h3>
            <p className="mt-2 text-xs leading-5 text-slate-400">
              Confirm the approval owner, deadline, and next meeting before the call loses
              momentum.
            </p>
          </div>

          <div className="rounded-xl border border-cyan-400/15 bg-cyan-500/[0.05] p-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-300">
              Ready to use
            </div>
            <h3 className="mt-3 font-semibold">CRM note prepared</h3>
            <p className="mt-2 text-xs leading-5 text-slate-400">
              First-person documentation shaped by the conversation and your Writing DNA.
            </p>
          </div>
        </div>

        <div className="border-t border-white/[0.07] px-5 py-4">
          <div className="flex items-center gap-3 text-sm text-slate-300">
            <CheckCircle size={18} weight="fill" className="text-emerald-400" />
            One connected output, ready for the next action.
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelinePreview() {
  return (
    <div className="relative rounded-2xl border border-white/[0.08] bg-[#09090d] p-5 shadow-2xl sm:p-7">
      <div className="flex items-center justify-between border-b border-white/[0.07] pb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
            Customer relationship
          </div>
          <div className="mt-1 font-semibold">Acme Industrial</div>
        </div>

        <div className="rounded-full border border-indigo-400/20 bg-indigo-500/[0.08] px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-indigo-200">
          3 conversations
        </div>
      </div>

      <div className="relative mt-6 space-y-5">
        <div className="absolute bottom-3 left-[0.43rem] top-3 w-px bg-white/[0.08]" />

        {timelineEvents.map((event, index) => (
          <div key={`${event.date}-${event.title}`} className="relative grid grid-cols-[1rem_1fr] gap-4">
            <div
              className={`relative z-10 mt-1.5 h-3.5 w-3.5 rounded-full border ${
                index === 0
                  ? "border-indigo-300 bg-indigo-400 shadow-[0_0_18px_rgba(129,140,248,0.45)]"
                  : "border-white/20 bg-[#09090d]"
              }`}
            />

            <article className="rounded-xl border border-white/[0.06] bg-white/[0.025] p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs text-slate-500">{event.date}</div>
                <div className="rounded-full border border-white/[0.08] px-2.5 py-1 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                  {event.tag}
                </div>
              </div>
              <h3 className="mt-3 font-semibold">{event.title}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-400">{event.copy}</p>
            </article>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-xl border border-indigo-400/15 bg-indigo-500/[0.06] p-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-indigo-300">
          Relationship shift
        </div>
        <p className="mt-2 text-sm leading-6 text-slate-300">
          The opportunity moved from product evaluation to internal approval. The next
          conversation should focus on decision ownership, not another value pitch.
        </p>
      </div>
    </div>
  );
}
